import { Box } from '@mui/material';

interface KpiValueBaseProps {
  /** Locale used by Intl.NumberFormat for numeric formatting. */
  locale?: string;
  /**
   * Text color for the whole value (theme tokens like `success.main` or CSS colors).
   * When omitted, suffixes (`%`, `/`, denominators, `h`/`m`/`s`) use `text.secondary`; the main digits follow the parent typography color.
   */
  color?: string;
}

export interface KpiValueNumberProps extends KpiValueBaseProps {
  type: 'number';
  value: number;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  notation?: 'standard' | 'compact';
  compactDisplay?: 'short' | 'long';
}

export interface KpiValuePercentageProps extends KpiValueBaseProps {
  type: 'percentage';
  /** Numeric percentage value, e.g. 99.32 => 99.32%. */
  value: number;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

export interface KpiValueRatioProps extends KpiValueBaseProps {
  type: 'ratio';
  /** Value before the slash; inherits the parent KPI typography (larger). */
  numerator: number;
  /** Value after the slash; rendered smaller when `color` is omitted. */
  denominator: number;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  notation?: 'standard' | 'compact';
  compactDisplay?: 'short' | 'long';
}

export interface KpiValueDurationProps extends KpiValueBaseProps {
  type: 'duration';
  /** Duration value in seconds. */
  valueSeconds: number;
  /** Defines which time units are rendered. */
  format?: 'hms' | 'hm' | 'ms';
  /** When true, adds left zero-padding for minute/second values. */
  padWithZero?: boolean;
}

export type KpiValueProps =
  | KpiValueNumberProps
  | KpiValuePercentageProps
  | KpiValueRatioProps
  | KpiValueDurationProps;

const DEFAULT_LOCALE = 'es-AR';

function formatNumber(
  value: number,
  locale: string,
  options: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    notation?: 'standard' | 'compact';
    compactDisplay?: 'short' | 'long';
  },
) {
  return new Intl.NumberFormat(locale, options).format(value);
}

/**
 * Splits duration into labeled parts and drops segments whose numeric value is zero
 * (e.g. 10s instead of 0h 0m 10s; 1h instead of 1h 0m 0s). When the total is zero,
 * returns a single `0` in the finest unit for that format.
 */
function formatDurationValue(
  valueSeconds: number,
  format: 'hms' | 'hm' | 'ms',
  padWithZero: boolean,
): { value: string; unit: 'h' | 'm' | 's' }[] {
  const totalSeconds = Math.max(0, Math.floor(valueSeconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const leftPad = (value: number) => (padWithZero ? String(value).padStart(2, '0') : String(value));

  let parts: { value: string; unit: 'h' | 'm' | 's' }[];

  if (format === 'hm') {
    parts = [
      { value: String(hours), unit: 'h' },
      { value: leftPad(minutes), unit: 'm' },
    ];
  } else if (format === 'ms') {
    const totalMinutes = Math.floor(totalSeconds / 60);
    parts = [
      { value: String(totalMinutes), unit: 'm' },
      { value: leftPad(seconds), unit: 's' },
    ];
  } else {
    parts = [
      { value: String(hours), unit: 'h' },
      { value: leftPad(minutes), unit: 'm' },
      { value: leftPad(seconds), unit: 's' },
    ];
  }

  const nonZeroParts = parts.filter((part) => Number(part.value) !== 0);

  if (nonZeroParts.length > 0) {
    return nonZeroParts;
  }

  if (format === 'hm') {
    return [{ value: '0', unit: 'm' }];
  }

  return [{ value: '0', unit: 's' }];
}

/**
 * Formats KPI values in a consistent way for numbers, percentages, ratios, and durations.
 * It is intended to be rendered inside `KpiBaseCard` value area.
 */
export function KpiValue(props: KpiValueProps) {
  const locale = props.locale ?? DEFAULT_LOCALE;
  const valueColorSx = props.color != null ? { color: props.color } : undefined;
  const suffixSx =
    props.color != null
      ? { typography: 'body2' as const }
      : { typography: 'body2' as const, color: 'text.secondary' as const };

  if (props.type === 'number') {
    return (
      <Box component="span" sx={valueColorSx}>
        {formatNumber(props.value, locale, {
          minimumFractionDigits: props.minimumFractionDigits,
          maximumFractionDigits: props.maximumFractionDigits,
          notation: props.notation,
          compactDisplay: props.compactDisplay,
        })}
      </Box>
    );
  }

  if (props.type === 'percentage') {
    const value = formatNumber(props.value, locale, {
      minimumFractionDigits: props.minimumFractionDigits,
      maximumFractionDigits: props.maximumFractionDigits,
    });

    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: 0.25,
          ...valueColorSx,
        }}
      >
        <Box component="span">{value}</Box>
        <Box component="span" sx={suffixSx}>
          %
        </Box>
      </Box>
    );
  }

  if (props.type === 'ratio') {
    const formatOpts = {
      minimumFractionDigits: props.minimumFractionDigits,
      maximumFractionDigits: props.maximumFractionDigits,
      notation: props.notation,
      compactDisplay: props.compactDisplay,
    };
    const numeratorText = formatNumber(props.numerator, locale, formatOpts);
    const denominatorText = formatNumber(props.denominator, locale, formatOpts);

    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: 0.25,
          ...valueColorSx,
        }}
      >
        <Box component="span">{numeratorText}</Box>
        <Box component="span" sx={suffixSx}>
          {' / '}
        </Box>
        <Box component="span" sx={suffixSx}>
          {denominatorText}
        </Box>
      </Box>
    );
  }

  const durationParts = formatDurationValue(
    props.valueSeconds,
    props.format ?? 'hms',
    props.padWithZero ?? true,
  );

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 1,
        ...valueColorSx,
      }}
    >
      {durationParts.map((part) => (
        <Box
          key={part.unit}
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.25 }}
        >
          <Box component="span">{part.value}</Box>
          <Box component="span" sx={suffixSx}>
            {part.unit}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
